import { c as _c } from "react/compiler-runtime";
import { useIntl } from '@onefootprint/hooks';
import { cx } from 'class-variance-authority';
import { useTranslation } from 'react-i18next';
import type { DeviceInsightEvent } from '@/components/entities/components/details/hooks/use-entity-device-insights';
import { useEntityContext } from '@/entity/hooks/use-entity-context';
import { displayForUserAgent, icoForUserAgent, isAppClip } from '@/utils/user-agent';
import useScrollIntoView from '../../hooks/use-scroll-into-view';
import getRegion from '../../utils/get-region';
import EmployeeOnlyIcon from '../employee-only-icon';
import AboutAppClipAndInstantApp from './components/about-app-clip-and-instant-app';
const ROW_CLASS_NAME = 'grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-3 gap-y-0.5 rounded-default px-3 py-2 text-left';
type DeviceRowProps = {
  id: string;
  event: DeviceInsightEvent;
  isEmployeeOnly?: boolean;
  isSelected: boolean;
  /** Changes with each pick of the row's pin, which scrolls the list to it. */
  mapPick?: number;
  /** Has a pin on the map; a row without one has nothing to select. */
  isLocatable: boolean;
  onSelect: (id: string) => void;
};
const DeviceRow = t0 => {
  const $0 = _c(59);
  const {
    id,
    event,
    isSelected,
    mapPick,
    isLocatable,
    onSelect,
    isEmployeeOnly
  } = t0;
  let t1;
  if ($0[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "deviceInsights"
    };
    $0[0] = t1;
  } else {
    t1 = $0[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const {
    formatDateWithTime
  } = useIntl();
  const context = useEntityContext();
  const ref = useScrollIntoView(mapPick);
  const {
    insight,
    kind,
    authEventKind
  } = event.event.data;
  const userAgent = insight.userAgent ?? "";
  const isPerson = context.kind === "person";
  let t2;
  if ($0[1] !== authEventKind || $0[2] !== isPerson || $0[3] !== kind || $0[4] !== t) {
    t2 = kind === "auth_event" ? isPerson && authEventKind && t($ => $.authEventKind[authEventKind]) : t($_0 => $_0.insightKind[kind]);
    $0[1] = authEventKind;
    $0[2] = isPerson;
    $0[3] = kind;
    $0[4] = t;
    $0[5] = t2;
  } else {
    t2 = $0[5];
  }
  const lead = t2;
  let t3;
  if ($0[6] !== insight.city || $0[7] !== insight.country || $0[8] !== insight.region || $0[9] !== isPerson) {
    t3 = isPerson ? [getRegion(insight.city, insight.region), insight.country].filter(Boolean).join(", ") : "";
    $0[6] = insight.city;
    $0[7] = insight.country;
    $0[8] = insight.region;
    $0[9] = isPerson;
    $0[10] = t3;
  } else {
    t3 = $0[10];
  }
  const place = t3;
  let t4;
  if ($0[11] !== insight.ipAddress || $0[12] !== lead || $0[13] !== place) {
    t4 = [lead, insight.ipAddress, place].filter(Boolean);
    $0[11] = insight.ipAddress;
    $0[12] = lead;
    $0[13] = place;
    $0[14] = t4;
  } else {
    t4 = $0[14];
  }
  const details = t4.join(" \xB7 ");
  const secondaryText = isSelected ? "text-secondary" : "text-tertiary";
  let t5;
  if ($0[15] !== userAgent) {
    t5 = icoForUserAgent(userAgent, "tertiary", "small");
    $0[15] = userAgent;
    $0[16] = t5;
  } else {
    t5 = $0[16];
  }
  let t6;
  if ($0[17] !== t5) {
    t6 = <span aria-hidden={true} className="row-span-2 mt-0.5 flex">{t5}</span>;
    $0[17] = t5;
    $0[18] = t6;
  } else {
    t6 = $0[18];
  }
  let t7;
  if ($0[19] !== userAgent) {
    t7 = displayForUserAgent(userAgent);
    $0[19] = userAgent;
    $0[20] = t7;
  } else {
    t7 = $0[20];
  }
  let t8;
  if ($0[21] !== isEmployeeOnly) {
    t8 = isEmployeeOnly && <span className="relative z-10 ml-1 inline-flex align-middle"><EmployeeOnlyIcon /></span>;
    $0[21] = isEmployeeOnly;
    $0[22] = t8;
  } else {
    t8 = $0[22];
  }
  let t9;
  if ($0[23] !== t7 || $0[24] !== t8) {
    t9 = <span className="truncate text-label-2 text-primary @max-md:col-span-2 @max-md:wrap-anywhere @max-md:whitespace-normal" data-dd-privacy="mask">{t7}{t8}</span>;
    $0[23] = t7;
    $0[24] = t8;
    $0[25] = t9;
  } else {
    t9 = $0[25];
  }
  let t10;
  if ($0[26] !== secondaryText) {
    t10 = cx("whitespace-nowrap text-body-3 @max-md:col-span-2 @max-md:col-start-2 @max-md:row-start-3", secondaryText);
    $0[26] = secondaryText;
    $0[27] = t10;
  } else {
    t10 = $0[27];
  }
  let t11;
  if ($0[28] !== formatDateWithTime || $0[29] !== insight.timestamp) {
    t11 = formatDateWithTime(new Date(insight.timestamp), {
      month: "short",
      year: "numeric"
    });
    $0[28] = formatDateWithTime;
    $0[29] = insight.timestamp;
    $0[30] = t11;
  } else {
    t11 = $0[30];
  }
  let t12;
  if ($0[31] !== t10 || $0[32] !== t11) {
    t12 = <span className={t10}>{t11}</span>;
    $0[31] = t10;
    $0[32] = t11;
    $0[33] = t12;
  } else {
    t12 = $0[33];
  }
  let t13;
  if ($0[34] !== details || $0[35] !== secondaryText) {
    t13 = details && <span className={cx("col-span-2 truncate text-body-3 @max-md:wrap-anywhere @max-md:whitespace-normal", secondaryText)} data-dd-privacy="mask">{details}</span>;
    $0[34] = details;
    $0[35] = secondaryText;
    $0[36] = t13;
  } else {
    t13 = $0[36];
  }
  let t14;
  if ($0[37] !== t12 || $0[38] !== t13 || $0[39] !== t6 || $0[40] !== t9) {
    t14 = <>{t6}{t9}{t12}{t13}</>;
    $0[37] = t12;
    $0[38] = t13;
    $0[39] = t6;
    $0[40] = t9;
    $0[41] = t14;
  } else {
    t14 = $0[41];
  }
  const content = t14;
  const t15 = isLocatable && (isSelected ? "bg-hover-control" : "hover:bg-hover");
  let t16;
  if ($0[42] !== t15) {
    t16 = cx("relative scroll-my-6 rounded-default transition-colors duration-100", t15);
    $0[42] = t15;
    $0[43] = t16;
  } else {
    t16 = $0[43];
  }
  let t17;
  if ($0[44] !== content || $0[45] !== id || $0[46] !== isLocatable || $0[47] !== isSelected || $0[48] !== onSelect) {
    t17 = isLocatable ? <button type="button" aria-current={isSelected || undefined} onClick={() => onSelect(id)} className={cx(ROW_CLASS_NAME, "cursor-pointer after:absolute after:inset-0 after:rounded-default")}>{content}</button> : <div className={ROW_CLASS_NAME}>{content}</div>;
    $0[44] = content;
    $0[45] = id;
    $0[46] = isLocatable;
    $0[47] = isSelected;
    $0[48] = onSelect;
    $0[49] = t17;
  } else {
    t17 = $0[49];
  }
  let t18;
  if ($0[50] !== secondaryText || $0[51] !== t || $0[52] !== userAgent) {
    t18 = isAppClip(userAgent) && <p className={cx("flex items-center gap-1 pr-3 pb-2 pl-10 text-body-3", secondaryText)}>{t(_temp)}<span className="relative z-10 flex"><AboutAppClipAndInstantApp kind="appClip" /></span></p>;
    $0[50] = secondaryText;
    $0[51] = t;
    $0[52] = userAgent;
    $0[53] = t18;
  } else {
    t18 = $0[53];
  }
  let t19;
  if ($0[54] !== ref || $0[55] !== t16 || $0[56] !== t17 || $0[57] !== t18) {
    t19 = <li ref={ref} className={t16}>{t17}{t18}</li>;
    $0[54] = ref;
    $0[55] = t16;
    $0[56] = t17;
    $0[57] = t18;
    $0[58] = t19;
  } else {
    t19 = $0[58];
  }
  return t19;
};
export default DeviceRow;
function _temp($_1) {
  return $_1.appClip.label;
}